/* @flow */
import * as React from 'react';

// Hooks
import { useButton, useId } from 'react-aria';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';

import messages from 'src/shared/app/nav/l10n/navigationL10n';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';
import styles from './MainNavSubMenu.style';

import MobileMenuIcon from 'src/shared/ui/component/icon/MobileMenuIcon';
import MainNavLink from './MainNavLink';
import { Popover } from '@mui/material';

const useStyles = createUseThemeStyles(styles);

type Props = {
  className?: string,
  items: React.Node[],
};

const MainNavSubMenu = ({ className, items }: Props): React.Node => {
  /*
   |------------------------------------------------------------------------------
   | States
   |------------------------------------------------------------------------------
   */
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const toggleMenu = () => setIsMobileMenuOpen((prevState) => !prevState);

  const anchorRef = React.useRef();

  /*
    |------------------------------------------------------------------------------
    | Hooks
    |------------------------------------------------------------------------------
  */
  const mobileMenuRef = React.useRef(null);
  const location = useLocation();
  const intl = useIntl();
  const { buttonProps } = useButton({
    onPress: toggleMenu,
  });

  const classes = useStyles();

  const subMenuId = useId();

  React.useEffect(() => {
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <li className={className} ref={anchorRef}>
      <button
        {...buttonProps}
        aria-expanded={isMobileMenuOpen}
        aria-controls={subMenuId}
        aria-label={intl.formatMessage(messages.submenu)}
        className={classes.item}
      >
        <MainNavLink
          title={intl.formatMessage(messages.more)}
          icon={<MobileMenuIcon />}
          disableActiveBackgroundColor
        />
      </button>

      <Popover
        id={subMenuId}
        open={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <ul className={classes.mobileMenu} ref={mobileMenuRef}>
          {items.map((item, key) => (
            <li className={classes.mobileMenuItem} key={key}>
              {item}
            </li>
          ))}
        </ul>
      </Popover>
    </li>
  );
};

export default React.memo<Props>(MainNavSubMenu);
